import {useRouter} from 'next/router';
import React, {useCallback, useEffect, useMemo} from 'react';

import {setupNavigation} from '@udemy/react-discovery-units';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {ConsumerSubscriptionPlan, useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';

import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {
    AUTH_FULL_PAGE_REDIRECT,
    COURSE_CARD_PREFETCH_FEATURE_CODE,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
    USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
    QUICK_VIEW_BOX_FEATURE,
    MX_MOBILE_VERTICAL_RECO_UNITS,
} from 'src/lib/experimentation-constants';

import {CategoryPage, CategoryProps} from './category.react-component';

export const categoryPageExperimentationFeatures = [
    AUTH_FULL_PAGE_REDIRECT,
    COURSE_CARD_PREFETCH_FEATURE_CODE,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
    USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
    QUICK_VIEW_BOX_FEATURE,
    MX_MOBILE_VERTICAL_RECO_UNITS,
];

export const CategoryWrapper: React.FC<
    Pick<CategoryProps, 'pageObject' | 'staticCategory' | 'staticDiscoveryUnits'>
> = ({pageObject, staticCategory, staticDiscoveryUnits}) => {
    const router = useRouter();
    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const {featureVariantAssignments: features} = useFeatures(categoryPageExperimentationFeatures);
    const {url: canonicalUrl} = useCanonicalUrl([''], META_DEFAULT_BASE_URL);

    const setup = useCallback(() => {
        setupNavigation(() => {
            const history = {
                ...globalThis.history,
                pushState: (
                    data: unknown,
                    unused: string,
                    url: string | URL | null | undefined,
                ) => {
                    if (!url) {
                        throw new Error(
                            'The url parameter passed to pushState cannot be undefined or null.',
                        );
                    }
                    const urlObj = new URL(url, canonicalUrl);
                    router.push(urlObj, undefined, {scroll: false, shallow: true});
                },
            };
            return [history, globalThis.location ?? {}];
        });
    }, [canonicalUrl, router]);

    useMemo(() => setup(), [setup]);

    useEffect(() => {
        setup();
    }, [setup]);

    const subsCollectionIds = useMemo(() => {
        const collectionIds =
            consumerSubscriptionPlan &&
            !consumerSubscriptionPlan?.isLoading &&
            consumerSubscriptionPlan?.isSubscriber
                ? (consumerSubscriptionPlan as unknown as ConsumerSubscriptionPlan)
                      ?.contentCollectionIds
                : undefined;
        if (Array.isArray(collectionIds)) {
            return collectionIds[0];
        }
        return collectionIds;
    }, [consumerSubscriptionPlan]);

    const showCodingExercisesBadge = false;

    const subscriptionCatalogDifferentiatorFeature =
        features.get(SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE)?.configuration ?? {};

    return (
        <CategoryPage
            pageObject={pageObject}
            staticCategory={staticCategory}
            subsCollectionIds={subsCollectionIds}
            showCodingExercisesBadge={showCodingExercisesBadge}
            staticDiscoveryUnits={staticDiscoveryUnits}
            subscriptionCatalogDifferentiatorFeature={subscriptionCatalogDifferentiatorFeature}
        />
    );
};
